import React, { FC, useState } from "react";

import { Text } from "@epignosis_llc/gnosis";

import { mobileCourseContentStyles } from "@components/CourseOverview/components/CourseContent/styles";

import { SendMessageMassActionDrawer } from "@views/Users";

import {
  isCourseButtonDisabled,
  isCourseButtonVisible,
} from "@components/CourseOverview/components/CourseHeader/CourseButton/helpers";
import { getCourseCategoryText } from "@components/CourseOverview/components/CourseHeader/helpers";
import { useApplyTranslations } from "@hooks";
import { useConfigurationStore } from "@stores";
import { getFlatUnits, getUnitIdToContinue, isCourseLevelRestricted } from "@utils/helpers";
import permissions from "@utils/permissions";

import Certificate from "@components/CourseOverview/components/CourseContent/Certificate";
import CourseTabs from "@components/CourseOverview/components/CourseContent/CourseTabs";
import CustomFields from "@components/CourseOverview/components/CourseContent/CustomFields";
import Description from "@components/CourseOverview/components/CourseContent/Description";
import Prerequisites from "@components/CourseOverview/components/CourseContent/Prerequisites";
import CourseButton from "@components/CourseOverview/components/CourseHeader/CourseButton/CourseButton";
import ExpiresDate from "@components/CourseOverview/components/CourseHeader/ExpiresDate";
import IntoVideo from "@components/CourseOverview/components/CourseHeader/IntoVideo";
import MobileBundles from "@components/CourseOverview/components/CourseHeader/MobileBundles";
import Progress from "@components/CourseOverview/components/CourseHeader/Progress";
import Rating from "@components/CourseOverview/components/CourseHeader/Rating";
import Restrictions from "@components/CourseOverview/components/CourseHeader/Restrictions";

import { Course, CourseCategory, Section } from "types/entities";

type MobileCourseContentProps = {
  course: Course;
  sections: Section[];
  isCatalogCourseView?: boolean;
  isCourseStore?: boolean;
  isAdminOverview?: boolean;
  categoryTree?: CourseCategory[];
};

const MobileCourseContent: FC<MobileCourseContentProps> = ({
  course,
  sections,
  isCatalogCourseView = false,
  isCourseStore = false,
  isAdminOverview = false,
  categoryTree = [],
}) => {
  const { catalogSettings, domainSettings } = useConfigurationStore();
  const { t } = useApplyTranslations();
  const [isSendMessageDrawerOpen, setSendMessageDrawerOpen] = useState(false);

  const {
    custom_fields = [],
    description,
    certificate,
    progress,
    rules,
    intro_video,
    name,
    bundles,
    enrolled,
    availability,
    level,
    user_access_expires_at,
    starts_at,
    expires_at,
    role_in_course,
    category,
  } = course;
  const { prerequisites } = rules;
  const { certificate: progressCertificate = null } = progress ?? {};
  const hasCertificate = Boolean(certificate) && !isCourseStore;
  const hasPrerequisites = prerequisites.length > 0 && !isCourseStore;
  const showBundleList = isCatalogCourseView && bundles && bundles.length > 0;
  const isUserEnrolled = Boolean(enrolled);
  const { completion_status } = progress ?? {};

  // Restrictions
  const { canSendMessageToInstructors } = permissions.messagesPermissions;
  const courseLevel = level ?? 0;
  const isLevelRestricted = isCourseLevelRestricted(courseLevel);
  const showSendInstructorMessage = canSendMessageToInstructors() && completion_status === "failed";

  // Course button
  const isSubscriptionEnabled = Boolean(catalogSettings?.subscription.enabled);
  const units = getFlatUnits(sections);
  const hasUnits = Boolean(units.length);
  const continueUnitId = hasUnits ? getUnitIdToContinue(units) : null;
  const isButtonVisible = isCourseButtonVisible({ isUserEnrolled, continueUnitId, availability });
  const isButtonDisabled = isCourseButtonDisabled({
    isSubscriptionEnabled,
    isCatalogCourseView,
    isLevelRestricted,
    availability,
    roleInCourse: role_in_course,
  });

  const { categoryText } = getCourseCategoryText({ category, categoryTree });
  const allowRating = Boolean(domainSettings?.course_rating);
  const isAvailable = availability && availability.is_available;
  const hasNoProgress = !progress || !isUserEnrolled || !isAvailable;
  const showButton = isButtonVisible;

  return (
    <div css={mobileCourseContentStyles}>
      <div className="content-wrapper">
        <div className="course-information-wrapper">
          {intro_video && (
            <div className="intro-video-wrapper">
              <IntoVideo introVideo={intro_video} name={name} isMobileView />
            </div>
          )}

          {allowRating && (
            <div className="rating-wrapper">
              <Rating course={course} isMobileView isCatalogCourseView={isCatalogCourseView} />
            </div>
          )}

          {!hasNoProgress && (
            <div className="progress-wrapper">
              <Progress
                isUserEnrolled={isUserEnrolled}
                progress={progress}
                availability={availability}
                isMobileView
                tagClassName="progress-info-tag"
              />
            </div>
          )}

          {(expires_at || user_access_expires_at) && (
            <div className="expiration-wrapper">
              <ExpiresDate
                expiresAt={user_access_expires_at ?? expires_at}
                tagClassName="expiration-info"
              />
            </div>
          )}

          {showSendInstructorMessage && (
            <Text fontSize="sm" as="div" className="instructor-text-info">
              <span onClick={(): void => setSendMessageDrawerOpen(true)}>
                {t("myCourses.contactInstructor")}
              </span>
            </Text>
          )}

          <Restrictions
            level={courseLevel}
            isLevelRestricted={isLevelRestricted}
            prerequisites={prerequisites}
            startsAt={starts_at}
            userAccessExpiresAt={user_access_expires_at}
            isMobileView
          />

          {showBundleList && (
            <div className="mobile-bundles-wrapper">
              <MobileBundles bundles={bundles} />
            </div>
          )}
        </div>

        {((custom_fields && custom_fields.length > 0) || description) && (
          <div className="description-container has-max-width">
            <CustomFields customFields={custom_fields} isMobileView />
            {description && <Description description={description} customLineClamp={5} />}
          </div>
        )}

        {hasCertificate && <Certificate acquiredCertificate={progressCertificate} />}

        <CourseTabs
          course={course}
          sections={sections}
          isCatalogCourseView={isCatalogCourseView}
          isAdminOverview={isAdminOverview}
        />
        {hasPrerequisites && <Prerequisites prerequisites={prerequisites} hideBorder />}
      </div>

      {showButton && (
        <div className="mobile-footer">
          <CourseButton
            course={course}
            isCatalogCourseView={isCatalogCourseView}
            isVisible={isButtonVisible}
            isDisabled={isButtonDisabled}
            isLevelRestricted={isLevelRestricted}
            categoryText={categoryText}
            continueUnitId={continueUnitId}
            isMobileView
          />
        </div>
      )}

      {isSendMessageDrawerOpen && (
        <SendMessageMassActionDrawer
          isDrawerOpen={isSendMessageDrawerOpen}
          onClose={(): void => setSendMessageDrawerOpen(false)}
          courseInstructorId={course.id}
          hideAttachments={true}
          headerText={t("myCourses.contactInstructor")}
        />
      )}
    </div>
  );
};

export default MobileCourseContent;
